/**
 * Radar Service — API client for the book-wide Change Radar (TASK-477)
 * Talks to alert-service /api/radar via the gateway proxy. Read-only: every
 * impact/exposure figure is server-computed (zero-LLM, grounded in positions).
 */

import { httpClient } from '../lib/httpClient';
import type { ActionPolicy } from '../types/dashboard';

export type { ActionPolicy };

export interface RadarSwapCandidate {
  candidate_isin: string | null;
  candidate_valor: string | null;
  fit_gain: number | null;
  dna_reason: string | null;
}

export interface ImpactedClient {
  client_id: string;
  /**
   * TASK-841: nullable, which the payload has always been able to be and this type denied.
   * `radar_service.py` used to substitute the client id when its `clients` lookup missed —
   * so the field was never null and was sometimes a UUID. It now sends null, and every
   * render site goes through `clientDisplayName`.
   */
  client_name: string | null;
  exposure_chf: number | null;
  exposure_pct: number | null;
  drift_caused: number | null;
  dna_note: string | null;
  suggested_action: string | null;
  /** The primary alert. Kept for existing consumers; `alert_ids` is the complete set. */
  alert_id: string | null;
  /** TASK-747: explicit name for the navigation target. */
  primary_alert_id?: string | null;
  /** TASK-747: EVERY contributing live alert for this client and subject. */
  alert_ids?: string[];
  /** TASK-729: the durable subject key — what work binds to, and what survives a rebuild. */
  subject_key?: string | null;
  /** TASK-747: the positions behind this exposure figure. */
  affected_position_ids?: string[];
  swap_candidate: RadarSwapCandidate | null;
}

export interface RadarEvent {
  id: string;
  action: string | null;
  entity_key: string | null;
  entity_type: string | null; // instrument | sector | client | macro
  entity_label: string | null;
  source: string | null; // news | cio | drift | dna
  event_ts: string | null;
  magnitude: number | null;
  impact_score: number | null;
  client_count: number;
  total_exposure_chf: number | null;
  impacted_clients: ImpactedClient[];
  suggested_batch_action: string | null;
  sources: unknown[] | null;
  /**
   * TASK-722: the RAW engineer's diagnostic. NEVER render this to an RM — it names entity
   * keys and internal counts. Use `unresolved_code` for user-facing copy and show this only
   * behind a support/dev affordance.
   */
  unresolved_reason: string | null;
  /** Stable reason code driving RM-facing copy. See `UNRESOLVED_COPY`. */
  unresolved_code?: 'zero_exposure' | 'no_entity' | 'dangling_client' | null;
  /** TASK-747: durable group identity, "{source_event}:{entity_key}". */
  group_key?: string | null;
  source_event?: string | null;
  news_url: string | null;
  normalized_score: number | null;
  /** TASK-731: the subject's nature — condition (outstanding matter) or occurrence. */
  subject_kind?: 'condition' | 'occurrence' | null;
  /** TASK-731: categorical mandatory band — ranks ahead of every advisory row. */
  mandatory?: boolean;
  sla_class?: string | null;
  sla_breached?: boolean;
  /**
   * TASK-822: the per-ROW SLA fact `sla_breached` could not express. `sla_breached` is its
   * boolean projection and is retained for one release.
   *   breached_by_nature  the source's existence IS the breach (kyc_expired).
   *   breached_derived    a due date the emitter supplied has passed — only as sound as
   *                       that date, so it is rendered distinguishably.
   *   undated             a mandatory obligation with no deadline. Not breached, not on track.
   *   within              dated, not yet due.
   */
  sla_state?: 'breached_by_nature' | 'breached_derived' | 'undated' | 'within' | string;
  /** TASK-822: when the OBLIGATION falls due. Distinct from `event_ts` (when the alert row
   *  was written) — a re-profile emitted 28 days ago can be 431 days overdue. */
  due_at?: string | null;
  /** Oldest unresolved detection in the group (event_ts stays the newest). */
  first_detected_at?: string | null;
  /**
   * TASK-731: true → CHF exposure is NOT KNOWN for this source. Render "not applicable";
   * never a bar, a zero, or any figure that reads as a valuation.
   */
  exposure_not_applicable?: boolean;
  /** TASK-732: the server-owned action contract this row's actions render from. */
  action_policy?: ActionPolicy | null;
}

export interface RadarResponse {
  /** The requested PAGE of events — never a basis for a total (TASK-725). */
  events: RadarEvent[];
  unresolved: RadarEvent[];
  total: number;
  /** Server-computed totals over every row. Optional so an older backend degrades to
   *  "unavailable" copy rather than to a silently wrong number. */
  summary?: RadarSummary;
  /**
   * TASK-924: the admitted catalogue's `source_event -> category` map (portfolio ·
   * compliance · client · document · news), shipped whole rather than per row so the Do
   * column can classify a signal-bound task whose condition has already cleared off the
   * Radar. Optional: an older backend degrades to an unfiltered lens, never to a wrong one.
   */
  categories?: Record<string, SignalCategory>;
}

/** TASK-924: the desk lens. Mirrors `RADAR_SOURCE_CATALOGUE`'s category set. */
export type SignalCategory = 'portfolio' | 'compliance' | 'client' | 'document' | 'news';
export const SIGNAL_CATEGORIES: readonly SignalCategory[] =
  ['portfolio', 'compliance', 'client', 'document', 'news'] as const;

export const radarService = {
  get: (limit = 20): Promise<RadarResponse> =>
    httpClient.get<RadarResponse>('/api/radar', { params: { limit } }),
};


/**
 * TASK-725: the six formal measures, computed SERVER-SIDE over every row before the page
 * slice. The Radar previously counted its own rendered array — already capped by
 * `useRadar(20)` and cut again by the tab filter — and labelled the result "positions".
 *
 * They are separate on purpose: collapsing them is how three surfaces came to report 11,
 * 123 and 15 for the same DNA truth while each was internally consistent.
 */
export interface RadarSourceSummary {
  radar_entity_groups: number;
  live_subjects: number;
  affected_clients: number;
  affected_positions: number;
  /** NOT summable with the overall figure — a position shared by two sources counts once
   *  in each. See the double-counting rule in `radar_service._summarize`. */
  exposure_chf: number;
}

export interface RadarSummary {
  /** The scope every number on this object is measured over. */
  scope: 'book';
  /** When the exposure figure was valued. */
  valued_at: string;
  radar_entity_groups: number;
  live_subjects: number;
  affected_clients: number;
  /** DISTINCT positions — a holding touched by two sources counts once. */
  affected_positions: number;
  exposure_chf: number;
  unresolved_groups: number;
  by_source: Record<string, RadarSourceSummary>;
}
